<template>
    <div class="package-quote-form">
        <KField
            label="Travel date"
            class="package-quote-form__group"
            required
        >
            <KDatePicker
                calendar-label="Travel date"
                :model-value="toCalendarDate(travelDate)"
                @update:model-value="value => travelDate = value ? value.toString() : ''"
            />
        </KField>

        <KField
            v-if="tierOptions.length"
            label="Hotel tier"
            class="package-quote-form__group"
        >
            <KSelect
                v-model="tier"
                :options="tierOptions"
                placeholder="Choose a hotel tier"
            />
        </KField>

        <KField
            v-if="isCouple"
            label="Guests"
            class="package-quote-form__group"
        >
            <p class="package-quote-form__fixed-guests">
                2 adults
            </p>
        </KField>

        <KFieldset
            v-else
            legend="Guests"
            class="package-quote-form__group"
        >
            <KField
                label="Adults"
                orientation="inline"
                class="package-quote-form__guest-row"
            >
                <KNumberField
                    :min="0"
                    :full-width="false"
                    :model-value="adults"
                    @update:model-value="value => setGuests('adults', value)"
                />
            </KField>
            <KField
                label="Children"
                orientation="inline"
                class="package-quote-form__guest-row"
            >
                <KNumberField
                    :min="0"
                    :full-width="false"
                    :model-value="children"
                    @update:model-value="value => setGuests('children', value)"
                />
            </KField>
        </KFieldset>

        <KFieldset
            v-if="displayedAddons.length"
            legend="Optional add-ons"
            class="package-quote-form__group"
        >
            <KCheckbox
                v-for="addon in displayedAddons"
                :key="addon._uid"
                :model-value="selectedAddons[addon._uid]"
                @update:model-value="value => selectedAddons[addon._uid] = value === true"
            >
                {{ addon.label }} (+${{ Number(addon.price) }}{{ addon.unit === 'per_person' ? ' pp' : '' }})
            </KCheckbox>
        </KFieldset>

        <p
            v-if="pricingNote"
            class="package-quote-form__pricing-note"
        >
            {{ pricingNote }}
        </p>

        <KLineItems
            :items="lineItems"
            :total="totalLine"
            :trailing="depositLine"
            aria-label="Price summary"
        />
    </div>
</template>

<script setup>
import { parseDate } from '@internationalized/date'
import { KCheckbox, KDatePicker, KField, KFieldset, KLineItems, KNumberField, KSelect } from '@vemedia/karibu'
import { computed } from 'vue'
import { formatPackageTier } from '~/composables/usePackageQuote'

// The quote configurator, rendered twice against ONE `usePackageQuote()` instance: once in
// the sidebar and once as the booking dialog's first step. Change a value in either and
// both follow, because neither owns the state.
//
// Deliberately free of Nuxt composables and auto-imports, which is what makes it mountable
// in plain vitest. The chrome around it — headings, calls to action, the block reason —
// belongs to whichever surface is rendering it.
const props = defineProps({
    /** A `usePackageQuote()` instance. */
    quote: {
        type: Object,
        required: true,
    },
    addons: {
        type: Array,
        default: () => [],
    },
    pricingNote: {
        type: String,
        default: '',
    },
})

const {
    travelDate,
    tier,
    tiers,
    adults,
    children,
    selectedAddons,
    setGuests,
    isCouple,
    breakdown,
    total,
    deposit,
    depositPercentage,
} = props.quote

const tierOptions = computed(() => tiers.map(value => ({
    value,
    label: formatPackageTier(value),
})))

const displayedAddons = computed(() =>
    props.addons.filter(addon => !isCouple || addon.unit !== 'per_person'),
)

const lineItems = computed(() => breakdown.value.map(line => ({
    label: line.label,
    value: `$${line.amount}`,
})))

const totalLine = computed(() => ({
    label: 'Estimated total',
    value: `$${total.value}`,
}))

const depositLine = computed(() => total.value > 0
    ? { label: `${depositPercentage}% deposit`, value: `$${deposit.value}` }
    : undefined)

function toCalendarDate(value) {
    if (!value)
        return undefined

    try {
        return parseDate(value)
    } catch {
        return undefined
    }
}
</script>

<style lang="scss" scoped>
.package-quote-form {
    &__group {
        display: block;
        margin-block-end: var(--space-4);
    }

    &__guest-row {
        grid-template-columns: 1fr auto;
        align-items: center;

        :deep(.k-field__control) {
            grid-area: 1 / 2;
            margin-block-start: 0;
        }

        :deep(input) {
            max-width: 60px;
        }
    }

    &__pricing-note {
        margin-block: var(--space-2);
        color: var(--color-text-muted);
        font-size: var(--font-size-1);
    }

    &__fixed-guests {
        margin: 0;
    }
}
</style>
